import { watch, onBeforeUnmount } from 'vue'
import { useAuthApiStore } from './authApiStore'

export interface AuthOrchestrator {
  verifyUser: () => Promise<unknown>
}

/**
 * Authentication orchestrator composable
 *
 * Coordinates between generic auth state and app-specific user profile stores
 * Automatically triggers user verification when authentication state changes
 * Automatically registers cleanup on component unmount
 *
 * @param orchestrator Object with verifyUser method from app-specific store
 */
export function useAuthOrchestrator(orchestrator: AuthOrchestrator) {
  const authApiStore = useAuthApiStore()

  // Watch for authentication changes and trigger user verification
  const stopWatcher = watch(
    () => authApiStore.isAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated && authApiStore.currentUserId) {
        await orchestrator.verifyUser()
      }
    },
    { immediate: true }
  )

  // Automatically register cleanup on component unmount
  onBeforeUnmount(() => {
    stopWatcher()
  })

  // Still return the stop function for manual control if needed
  return {
    stopAuthWatcher: stopWatcher,
  }
}
